import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
    Plus,
    Receipt,
    Check,
    X,
    Calendar,
    Building,
    Trash2,
    CheckCircle,
    AlertCircle,
    Copy,
    Edit,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

function Gastos() {
    const { user } = useAuth();
    const [gastos, setGastos] = useState([]);
    const [habitaciones, setHabitaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedGasto, setSelectedGasto] = useState(null);
    const [filtros, setFiltros] = useState({
        habitacion_id: "",
        tipo_gasto: "",
        mes: new Date().getMonth() + 1,
        anio: new Date().getFullYear(),
    });

    const getFechasMesAnterior = () => {
        const hoy = new Date();
        const anio = hoy.getFullYear();
        const mes = hoy.getMonth(); // 0-11

        // Primer día del mes anterior
        const inicioMesAnterior = new Date(anio, mes - 1, 1);
        // Último día del mes anterior (vamos al primer día del mes actual y restamos un día)
        const finMesAnterior = new Date(anio, mes, 0);

        const formatear = (fecha) => fecha.toISOString().split("T")[0];

        return {
            inicio: formatear(inicioMesAnterior),
            fin: formatear(finMesAnterior),
        };
    };

    const [formData, setFormData] = useState({
        concepto: "",
        importe_total: "",
        tipo_gasto: "otros",
        habitacion_id: "",
        fecha_cargo: new Date().toISOString().split("T")[0],
        numero_factura: "",
        fecha_emision: new Date().toISOString().split("T")[0], // Fecha de hoy por defecto
        periodo_facturacion_inicio: getFechasMesAnterior().inicio,
        periodo_facturacion_fin: getFechasMesAnterior().fin,
        distribucion_tipo: "ocupantes",
        descripcion: "",
    });

    const [editFormData, setEditFormData] = useState({
        concepto: "",
        importe_total: "",
        tipo_gasto: "otros",
        habitacion_id: "",
        fecha_cargo: "",
        numero_factura: "",
        fecha_emision: "",
        periodo_facturacion_inicio: "",
        periodo_facturacion_fin: "",
        distribucion_tipo: "ocupantes",
        descripcion: "",
    });

    const [pagoData, setPagoData] = useState({
        estado_pago: "pagado",
        fecha_pago: new Date().toISOString().split("T")[0],
        metodo_pago: "",
        notas: "",
    });

    const cargarDatos = useCallback(async () => {
        try {
            setLoading(true);

            // Cargar gastos con detalles de pago
            const gastosResponse = await axios.get("/api/pagos/detalle-gastos", {
                params: filtros,
            });
            setGastos(gastosResponse.data);

            // Cargar habitaciones si es gestor
            if (user?.tipo_usuario === "gestor") {
                const habitacionesResponse = await axios.get("/api/habitaciones");
                setHabitaciones(habitacionesResponse.data);
            }
        } catch (error) {
            console.error("Error al cargar gastos:", error);
        } finally {
            setLoading(false);
        }
    }, [filtros, user?.tipo_usuario]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    // Navegar al mes anterior
    const irMesAnterior = () => {
        let nuevoMes = filtros.mes - 1;
        let nuevoAnio = filtros.anio;

        if (nuevoMes < 1) {
            nuevoMes = 12;
            nuevoAnio = nuevoAnio - 1;
        }

        setFiltros({
            ...filtros,
            mes: nuevoMes,
            anio: nuevoAnio,
        });
    };

    // Navegar al mes siguiente
    const irMesSiguiente = () => {
        let nuevoMes = filtros.mes + 1;
        let nuevoAnio = filtros.anio;

        if (nuevoMes > 12) {
            nuevoMes = 1;
            nuevoAnio = nuevoAnio + 1;
        }

        setFiltros({
            ...filtros,
            mes: nuevoMes,
            anio: nuevoAnio,
        });
    };

    const copiarMensajeWhatsApp = async (gasto) => {
        try {
            const tipoGastoTexto = tiposGasto[gasto.tipo_gasto];

            let periodoTexto = "";
            if (gasto.periodo_facturacion_inicio && gasto.periodo_facturacion_fin) {
                const fechaInicio = new Date(gasto.periodo_facturacion_inicio);
                const fechaFin = new Date(gasto.periodo_facturacion_fin);

                const formatoMes = (fecha) => {
                    return fecha
                        .toLocaleDateString("es-ES", {
                            month: "long",
                            year: "numeric",
                        })
                        .replace(/^\w/, (c) => c.toUpperCase());
                };

                periodoTexto = ` de ${formatoMes(fechaInicio)}-${formatoMes(fechaFin)}`;
            }

            const mensaje = `Buenas tardes,
Os adjunto la factura de ${tipoGastoTexto}${periodoTexto}.
La parte proporcional sería de ${formatearMoneda(gasto.importe_total).replace(
                "€",
                ""
            )}€`;

            await navigator.clipboard.writeText(mensaje);

            // Mostrar feedback visual
            if (document.activeElement) {
                document.activeElement.style.backgroundColor = "#10b981";
                setTimeout(() => {
                    if (document.activeElement) {
                        document.activeElement.style.backgroundColor = "";
                    }
                }, 1000);
            }

            alert("Mensaje copiado al portapapeles");
        } catch (error) {
            console.error("Error al copiar al portapapeles:", error);
            alert("Error al copiar el mensaje");
        }
    };

    const handleEditSubmit = async () => {
        try {
            await axios.put(`/api/gastos/${selectedGasto.id}`, editFormData);
            setShowEditModal(false);
            setSelectedGasto(null);
            cargarDatos();
            alert("Gasto actualizado exitosamente");
        } catch (error) {
            console.error("Error al actualizar gasto:", error);
            alert("Error al actualizar el gasto");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await axios.post("/api/gastos", formData);
            setShowModal(false);
            setFormData({
                concepto: "",
                importe_total: "",
                tipo_gasto: "otros",
                habitacion_id: "",
                fecha_cargo: new Date().toISOString().split("T")[0], // Mantener fecha de hoy
                numero_factura: "",
                fecha_emision: new Date().toISOString().split("T")[0], // Mantener fecha de hoy
                periodo_facturacion_inicio: getFechasMesAnterior().inicio,
                periodo_facturacion_fin: getFechasMesAnterior().fin,
                distribucion_tipo: "ocupantes",
                descripcion: "",
            });
            cargarDatos();
        } catch (error) {
            console.error("Error al crear gasto:", error);
            alert("Error al crear el gasto");
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();

        try {
            await axios.post("/api/pagos", {
                gasto_id: selectedGasto.id,
                ...pagoData,
            });

            setShowPaymentModal(false);
            setSelectedGasto(null);
            setPagoData({
                estado_pago: "pagado",
                fecha_pago: new Date().toISOString().split("T")[0],
                metodo_pago: "",
                notas: "",
            });
            cargarDatos();
        } catch (error) {
            console.error("Error al registrar pago:", error);
            alert("Error al registrar el pago");
        }
    };

    const generarMensualidades = async () => {
        const confirmacion = window.confirm(
            `¿Generar mensualidades para ${meses[filtros.mes - 1]} ${
                filtros.anio
            }?\n\n⚠️ Solo se generarán mensualidades para habitaciones ocupadas (con inquilinos asignados). Las habitaciones vacías serán omitidas automáticamente.`
        );

        if (confirmacion) {
            try {
                const response = await axios.post("/api/gastos/generar-mensualidades", {
                    mes: filtros.mes,
                    anio: filtros.anio,
                    fecha_cargo: `${filtros.anio}-${String(filtros.mes).padStart(
                        2,
                        "0"
                    )}-05`,
                });

                cargarDatos();

                const { data } = response;
                let mensaje = `✅ Mensualidades de ${meses[filtros.mes - 1]} ${
                    filtros.anio
                } generadas exitosamente!\n\n`;
                mensaje += `📊 Resumen:\n`;
                mensaje += `• Habitaciones ocupadas: ${data.habitaciones_ocupadas}\n`;
                if (data.habitaciones_vacias_omitidas > 0) {
                    mensaje += `• Habitaciones vacías omitidas: ${data.habitaciones_vacias_omitidas}\n`;
                }
                mensaje += `\n📋 Detalles:\n`;
                data.detalles.forEach((detalle) => {
                    mensaje += `• ${detalle.habitacion}: ${detalle.inquilinos} inquilino${
                        detalle.inquilinos > 1 ? "s" : ""
                    } - ${formatearMoneda(detalle.mensualidad)}\n`;
                });

                alert(mensaje);
            } catch (error) {
                console.error("Error al generar mensualidades:", error);
                alert(
                    error.response?.data?.message || "Error al generar mensualidades"
                );
            }
        }
    };

    const eliminarGasto = async (gastoId) => {
        const confirmacion = window.confirm(
            "¿Estás seguro de que quieres eliminar este gasto?"
        );

        if (confirmacion) {
            try {
                await axios.delete(`/api/gastos/${gastoId}`);
                cargarDatos();
            } catch (error) {
                console.error("Error al eliminar gasto:", error);
                alert("Error al eliminar el gasto");
            }
        }
    };

    const openEditGastoModal = (gasto) => {
        const formatDateForInput = (dateString) => {
            if (!dateString) return "";
            // Asegurarse de que la fecha se interpreta correctamente antes de formatear
            return new Date(dateString).toISOString().split("T")[0];
        };

        setSelectedGasto(gasto);
        setEditFormData({
            ...gasto,
            fecha_cargo: formatDateForInput(gasto.fecha_cargo),
            fecha_emision: formatDateForInput(gasto.fecha_emision),
            periodo_facturacion_inicio: formatDateForInput(
                gasto.periodo_facturacion_inicio
            ),
            periodo_facturacion_fin: formatDateForInput(
                gasto.periodo_facturacion_fin
            ),
        });
        setShowEditModal(true);
    };

    const formatearMoneda = (cantidad) => {
        return new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
        }).format(cantidad);
    };

    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString("es-ES");
    };

    const meses = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
    ];

    const tiposGasto = {
        mensualidad: "Mensualidad",
        agua: "Agua",
        luz: "Electricidad",
        otros: "Otros",
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>Gestión de Gastos</h1>
                    <p>Administra los gastos del piso</p>
                </div>

                {user?.tipo_usuario === "gestor" && (
                    <div className="header-actions">
                        <button
                            onClick={generarMensualidades}
                            className="btn btn-secondary"
                        >
                            <Calendar size={16} />
                            Generar Mensualidades
                        </button>
                        <button
                            onClick={() => setShowModal(true)}
                            className="btn btn-primary"
                        >
                            <Plus size={16} />
                            Nuevo Gasto
                        </button>
                    </div>
                )}
            </div>

            {/* Filtros */}
            <div className="card filters">
                <h3>Filtros</h3>
                <div className="filters-grid">
                    {/* Navegación de Mes */}
                    <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                        <label className="form-label">Navegación de Mes</label>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <button
                                onClick={irMesAnterior}
                                className="btn btn-secondary"
                                style={{ flex: "0 0 auto" }}
                                title="Mes anterior"
                            >
                                <ChevronLeft size={16} />
                                Mes Anterior
                            </button>
                            <div
                                style={{
                                    flex: "1",
                                    textAlign: "center",
                                    fontSize: "18px",
                                    fontWeight: "600",
                                    color: "#1f2937",
                                }}
                            >
                                {meses[filtros.mes - 1]} {filtros.anio}
                            </div>
                            <button
                                onClick={irMesSiguiente}
                                className="btn btn-secondary"
                                style={{ flex: "0 0 auto" }}
                                title="Mes siguiente"
                            >
                                Mes Siguiente
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Mes</label>
                        <select
                            value={filtros.mes}
                            onChange={(e) =>
                                setFiltros({
                                    ...filtros,
                                    mes: parseInt(e.target.value),
                                })
                            }
                            className="form-select"
                        >
                            {meses.map((mes, index) => (
                                <option key={index} value={index + 1}>
                                    {mes}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Año</label>
                        <select
                            value={filtros.anio}
                            onChange={(e) =>
                                setFiltros({
                                    ...filtros,
                                    anio: parseInt(e.target.value),
                                })
                            }
                            className="form-select"
                        >
                            <option value={2023}>2023</option>
                            <option value={2024}>2024</option>
                            <option value={2025}>2025</option>
                            <option value={2026}>2026</option>
                            <option value={2027}>2027</option>
                            <option value={2028}>2028</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Tipo de Gasto</label>
                        <select
                            value={filtros.tipo_gasto}
                            onChange={(e) =>
                                setFiltros({
                                    ...filtros,
                                    tipo_gasto: e.target.value,
                                })
                            }
                            className="form-select"
                        >
                            <option value="">Todos</option>
                            {Object.entries(tiposGasto).map(([key, value]) => (
                                <option key={key} value={key}>
                                    {value}
                                </option>
                            ))}
                        </select>
                    </div>

                    {user?.tipo_usuario === "gestor" && (
                        <div className="form-group">
                            <label className="form-label">Habitación</label>
                            <select
                                value={filtros.habitacion_id}
                                onChange={(e) =>
                                    setFiltros({
                                        ...filtros,
                                        habitacion_id: e.target.value,
                                    })
                                }
                                className="form-select"
                            >
                                <option value="">Todas</option>
                                {habitaciones.map((habitacion) => (
                                    <option key={habitacion.id} value={habitacion.id}>
                                        {habitacion.numero}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Lista de gastos */}
            <div className="card">
                <h2>
                    Gastos - {meses[filtros.mes - 1]} {filtros.anio}
                </h2>
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Concepto</th>
                                <th>Tipo</th>
                                <th>Habitación</th>
                                <th>Importe</th>
                                <th>Estado</th>
                                <th>Fecha Pago</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gastos.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="7"
                                        style={{
                                            textAlign: "center",
                                            padding: "40px",
                                        }}
                                    >
                                        <Receipt
                                            size={48}
                                            style={{
                                                color: "#d1d5db",
                                                marginBottom: "16px",
                                            }}
                                        />
                                        <p style={{ color: "#6b7280" }}>
                                            No hay gastos para este período
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                gastos.map((gasto) => (
                                    <tr key={gasto.id}>
                                        <td data-label="Concepto">
                                            <div>
                                                <div
                                                    style={{
                                                        fontWeight: "500",
                                                    }}
                                                >
                                                    {gasto.concepto}
                                                </div>
                                                {gasto.descripcion && (
                                                    <div
                                                        style={{
                                                            fontSize: "12px",
                                                            color: "#6b7280",
                                                        }}
                                                    >
                                                        {gasto.descripcion}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td data-label="Tipo">
                                            <span className="badge badge-secondary">
                                                {tiposGasto[gasto.tipo_gasto]}
                                            </span>
                                        </td>
                                        <td data-label="Habitación">
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                }}
                                            >
                                                <Building size={16} />
                                                {gasto.habitacion_numero}
                                            </div>
                                        </td>
                                        <td data-label="Importe" style={{ fontWeight: "600" }}>
                                            {formatearMoneda(gasto.importe_total)}
                                        </td>
                                        <td data-label="Estado">
                                            <span
                                                className={`badge ${
                                                    gasto.estado_pago === "pagado"
                                                        ? "badge-success"
                                                        : "badge-warning"
                                                }`}
                                            >
                                                {gasto.estado_pago === "pagado" ? (
                                                    <>
                                                        <CheckCircle size={12} />
                                                        Pagado
                                                    </>
                                                ) : (
                                                    <>
                                                        <AlertCircle size={12} />
                                                        Pendiente
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                        <td data-label="Fecha Pago">
                                            {gasto.fecha_pago
                                                ? formatearFecha(gasto.fecha_pago)
                                                : "-"}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    onClick={() => copiarMensajeWhatsApp(gasto)}
                                                    className="btn-icon btn-copy"
                                                    title="Copiar mensaje para WhatsApp"
                                                >
                                                    <Copy size={16} />
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setSelectedGasto(gasto);
                                                        setShowPaymentModal(true);
                                                        setPagoData({
                                                            ...pagoData,
                                                            estado_pago:
                                                                gasto.estado_pago === "pagado"
                                                                    ? "pendiente"
                                                                    : "pagado",
                                                        });
                                                    }}
                                                    className={`btn-icon ${
                                                        gasto.estado_pago === "pagado"
                                                            ? "btn-warning"
                                                            : "btn-success"
                                                    }`}
                                                    title={
                                                        gasto.estado_pago === "pagado"
                                                            ? "Marcar como pendiente"
                                                            : "Marcar como pagado"
                                                    }
                                                >
                                                    {gasto.estado_pago === "pagado" ? (
                                                        <X size={16} />
                                                    ) : (
                                                        <Check size={16} />
                                                    )}
                                                </button>

                                                {user?.tipo_usuario === "gestor" && (
                                                    <button
                                                        onClick={() => openEditGastoModal(gasto)}
                                                        className="btn-icon btn-info"
                                                        title="Editar gasto"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                )}

                                                {user?.tipo_usuario === "gestor" && (
                                                    <button
                                                        onClick={() => eliminarGasto(gasto.id)}
                                                        className="btn-icon btn-danger"
                                                        title="Eliminar gasto"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal crear gasto */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Nuevo Gasto</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Concepto *</label>
                                <input
                                    type="text"
                                    value={formData.concepto}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            concepto: e.target.value,
                                        })
                                    }
                                    className="form-input"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2">
                                <div className="form-group">
                                    <label className="form-label">Importe Total *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.importe_total}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                importe_total: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Tipo de Gasto *</label>
                                    <select
                                        value={formData.tipo_gasto}
                                        onChange={(e) => {
                                            const selectedTipo = e.target.value;
                                            // Auto-completar concepto basado en el tipo de gasto
                                            let conceptoAuto = formData.concepto;
                                            if (selectedTipo === "agua") {
                                                conceptoAuto = "Agua";
                                            } else if (selectedTipo === "luz") {
                                                conceptoAuto = "Electricidad";
                                            } else if (selectedTipo === "mensualidad") {
                                                conceptoAuto = "Mensualidad";
                                            }

                                            setFormData({
                                                ...formData,
                                                tipo_gasto: selectedTipo,
                                                concepto: conceptoAuto,
                                            });
                                        }}
                                        className="form-select"
                                        required
                                    >
                                        {Object.entries(tiposGasto).map(([key, value]) => (
                                            <option key={key} value={key}>
                                                {value}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {(formData.tipo_gasto === "mensualidad" ||
                                formData.tipo_gasto === "otros") && (
                                <div className="form-group">
                                    <label className="form-label">Habitación *</label>
                                    <select
                                        value={formData.habitacion_id}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                habitacion_id: e.target.value,
                                            })
                                        }
                                        className="form-select"
                                        required
                                    >
                                        <option value="">Seleccionar habitación</option>
                                        {habitaciones.map((habitacion) => (
                                            <option key={habitacion.id} value={habitacion.id}>
                                                {habitacion.numero}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Datos de Factura */}
                            <div className="form-section">
                                <h4>Datos de Factura</h4>

                                <div className="form-group">
                                    <label className="form-label">Número de Factura</label>
                                    <input
                                        type="text"
                                        value={formData.numero_factura}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                numero_factura: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        placeholder="Ej: FAC-2025-001"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Fecha de Cargo</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_cargo}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                fecha_cargo: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Fecha de Emisión</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_emision}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                fecha_emision: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Periodo Facturación - Inicio
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.periodo_facturacion_inicio}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                periodo_facturacion_inicio: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        required={
                                            formData.tipo_gasto === "agua" ||
                                            formData.tipo_gasto === "luz"
                                        }
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Periodo Facturación - Fin
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.periodo_facturacion_fin}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                periodo_facturacion_fin: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        required={
                                            formData.tipo_gasto === "agua" ||
                                            formData.tipo_gasto === "luz"
                                        }
                                    />
                                </div>

                                {(formData.tipo_gasto === "agua" ||
                                    formData.tipo_gasto === "luz") && (
                                    <div className="form-group">
                                        <label className="form-label">Tipo de Distribución</label>
                                        <select
                                            value={formData.distribucion_tipo}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    distribucion_tipo: e.target.value,
                                                })
                                            }
                                            className="form-select"
                                        >
                                            <option value="ocupantes">Por ocupantes</option>
                                            <option value="habitaciones">
                                                Por habitaciones (incluye vacías)
                                            </option>
                                        </select>
                                        <small className="form-help">
                                            {formData.distribucion_tipo === "ocupantes"
                                                ? "Se distribuye proporcionalmente según número de inquilinos"
                                                : "Se distribuye equitativamente entre todas las habitaciones"}
                                        </small>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Descripción</label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            descripcion: e.target.value,
                                        })
                                    }
                                    className="form-textarea"
                                    rows="3"
                                />
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="btn btn-outline"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Crear Gasto
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal registrar pago */}
            {showPaymentModal && selectedGasto && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowPaymentModal(false)}
                >
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Registrar Pago</h2>
                        <div className="gasto-details">
                            <h3>{selectedGasto.concepto}</h3>
                            <p>
                                {formatearMoneda(selectedGasto.importe_total)} •{" "}
                                {selectedGasto.habitacion_numero}
                            </p>
                        </div>

                        <form onSubmit={handlePayment}>
                            <div className="form-group">
                                <label className="form-label">Estado del Pago</label>
                                <select
                                    value={pagoData.estado_pago}
                                    onChange={(e) =>
                                        setPagoData({
                                            ...pagoData,
                                            estado_pago: e.target.value,
                                        })
                                    }
                                    className="form-select"
                                >
                                    <option value="pagado">Pagado</option>
                                    <option value="pendiente">Pendiente</option>
                                </select>
                            </div>

                            {pagoData.estado_pago === "pagado" && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Fecha de Pago</label>
                                        <input
                                            type="date"
                                            value={pagoData.fecha_pago}
                                            onChange={(e) =>
                                                setPagoData({
                                                    ...pagoData,
                                                    fecha_pago: e.target.value,
                                                })
                                            }
                                            className="form-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Método de Pago</label>
                                        <select
                                            value={pagoData.metodo_pago}
                                            onChange={(e) =>
                                                setPagoData({
                                                    ...pagoData,
                                                    metodo_pago: e.target.value,
                                                })
                                            }
                                            className="form-select"
                                        >
                                            <option value="">Selecciona método</option>
                                            <option value="Transferencia">Transferencia</option>
                                            <option value="Bizum">Bizum</option>
                                            <option value="Efectivo">Efectivo</option>
                                            <option value="Otros">Otros (introducir en Notas)</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="form-group">
                                <label className="form-label">Notas</label>
                                <textarea
                                    value={pagoData.notas}
                                    onChange={(e) =>
                                        setPagoData({
                                            ...pagoData,
                                            notas: e.target.value,
                                        })
                                    }
                                    className="form-textarea"
                                    rows="3"
                                    placeholder="Observaciones adicionales..."
                                />
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={() => setShowPaymentModal(false)}
                                    className="btn btn-outline"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Registrar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal editar gasto */}
            {showEditModal && selectedGasto && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Editar Gasto</h2>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleEditSubmit();
                            }}
                        >
                            <div className="form-group">
                                <label className="form-label">Concepto *</label>
                                <input
                                    type="text"
                                    value={editFormData.concepto}
                                    onChange={(e) =>
                                        setEditFormData({
                                            ...editFormData,
                                            concepto: e.target.value,
                                        })
                                    }
                                    className="form-input"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2">
                                <div className="form-group">
                                    <label className="form-label">Importe Total *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editFormData.importe_total}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                importe_total: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Tipo de Gasto *</label>
                                    <select
                                        value={editFormData.tipo_gasto}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                tipo_gasto: e.target.value,
                                            })
                                        }
                                        className="form-select"
                                        required
                                    >
                                        {Object.entries(tiposGasto).map(([key, value]) => (
                                            <option key={key} value={key}>
                                                {value}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {(editFormData.tipo_gasto === "mensualidad" ||
                                editFormData.tipo_gasto === "otros") && (
                                <div className="form-group">
                                    <label className="form-label">Habitación *</label>
                                    <select
                                        value={editFormData.habitacion_id}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                habitacion_id: e.target.value,
                                            })
                                        }
                                        className="form-select"
                                        required
                                    >
                                        <option value="">Seleccionar habitación</option>
                                        {habitaciones.map((habitacion) => (
                                            <option key={habitacion.id} value={habitacion.id}>
                                                {habitacion.numero}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Datos de Factura */}
                            <div className="form-section">
                                <h4>Datos de Factura</h4>

                                <div className="form-group">
                                    <label className="form-label">Número de Factura</label>
                                    <input
                                        type="text"
                                        value={editFormData.numero_factura}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                numero_factura: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        placeholder="Ej: FAC-2025-001"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Fecha de Emisión</label>
                                    <input
                                        type="date"
                                        value={editFormData.fecha_emision}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                fecha_emision: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Periodo Facturación - Inicio
                                    </label>
                                    <input
                                        type="date"
                                        value={editFormData.periodo_facturacion_inicio}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                periodo_facturacion_inicio: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        required={
                                            editFormData.tipo_gasto === "agua" ||
                                            editFormData.tipo_gasto === "luz"
                                        }
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Periodo Facturación - Fin
                                    </label>
                                    <input
                                        type="date"
                                        value={editFormData.periodo_facturacion_fin}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                periodo_facturacion_fin: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                        required={
                                            editFormData.tipo_gasto === "agua" ||
                                            editFormData.tipo_gasto === "luz"
                                        }
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Fecha de Cargo</label>
                                    <input
                                        type="date"
                                        value={editFormData.fecha_cargo}
                                        onChange={(e) =>
                                            setEditFormData({
                                                ...editFormData,
                                                fecha_cargo: e.target.value,
                                            })
                                        }
                                        className="form-input"
                                    />
                                </div>

                                {(editFormData.tipo_gasto === "agua" ||
                                    editFormData.tipo_gasto === "luz") && (
                                    <div className="form-group">
                                        <label className="form-label">Tipo de Distribución</label>
                                        <select
                                            value={editFormData.distribucion_tipo}
                                            onChange={(e) =>
                                                setEditFormData({
                                                    ...editFormData,
                                                    distribucion_tipo: e.target.value,
                                                })
                                            }
                                            className="form-select"
                                        >
                                            <option value="ocupantes">Por ocupantes</option>
                                            <option value="habitaciones">
                                                Por habitaciones (incluye vacías)
                                            </option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Descripción</label>
                                <textarea
                                    value={editFormData.descripcion}
                                    onChange={(e) =>
                                        setEditFormData({
                                            ...editFormData,
                                            descripcion: e.target.value,
                                        })
                                    }
                                    className="form-textarea"
                                    rows="3"
                                />
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={() => setShowEditModal(false)}
                                    className="btn btn-outline"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Guardar Cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 24px;
                }

                .page-header h1 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 8px;
                }

                .page-header p {
                    color: #6b7280;
                }

                .header-actions {
                    display: flex;
                    gap: 12px;
                }

                .filters {
                    margin-bottom: 24px;
                }

                .filters h3 {
                    margin-bottom: 16px;
                    color: #374151;
                }

                .filters-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                }

                .table-container {
                    overflow-x: auto;
                }

                .action-buttons {
                    display: flex;
                    gap: 8px;
                }

                .btn-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-icon.btn-success {
                    background-color: #10b981;
                    color: white;
                }

                .btn-icon.btn-warning {
                    background-color: #f59e0b;
                    color: white;
                }

                .btn-icon.btn-danger {
                    background-color: #ef4444;
                    color: white;
                }

                .btn-icon.btn-info {
                    background-color: #3b82f6;
                    color: white;
                }

                .btn-icon.btn-copy {
                    background-color: #8b5cf6;
                    color: white;
                }

                .btn-icon:hover {
                    transform: scale(1.1);
                }

                .gasto-details {
                    background: #f9fafb;
                    padding: 16px;
                    border-radius: 8px;
                    margin-bottom: 24px;
                    border: 1px solid #e5e7eb;
                }

                .gasto-details h3 {
                    margin-bottom: 4px;
                    color: #1f2937;
                }

                .gasto-details p {
                    color: #6b7280;
                    font-size: 14px;
                }

                .modal-actions {
                    display: flex;
                    gap: 12px;
                    justify-content: flex-end;
                    margin-top: 24px;
                }

                @media (max-width: 768px) {
                    .page-header {
                        flex-direction: column;
                        gap: 16px;
                    }

                    .header-actions {
                        width: 100%;
                        justify-content: stretch;
                    }

                    .header-actions .btn {
                        flex: 1;
                    }

                    .filters-grid {
                        grid-template-columns: 1fr;
                    }

                    .modal {
                        margin: 20px;
                        /* max-width: none; */
                    }
                    .table-container {
                        border: none;
                        box-shadow: none;
                    }

                    .table {
                        border: none;
                        box-shadow: none;
                    }

                    .table thead {
                        display: none;
                    }

                    .table tbody,
                    .table tr,
                    .table td {
                        display: block;
                    }

                    .table tr {
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        margin-bottom: 16px;
                        padding: 8px 16px;
                    }

                    .table td {
                        padding: 10px 0;
                        border: none;
                        border-bottom: 1px solid #f3f4f6;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        text-align: right;
                        font-size: 14px;
                    }

                    .table tr td:last-child {
                        border-bottom: none;
                    }

                    .table td:before {
                        content: attr(data-label);
                        font-weight: 600;
                        color: #374151;
                        text-align: left;
                        margin-right: 16px;
                        font-size: 14px;
                    }

                    .action-buttons {
                        justify-content: flex-end;
                        flex-wrap: wrap;
                    }

                    .table td:last-child {
                        padding-top: 16px;
                        padding-bottom: 8px;
                    }

                    .table td:last-child:before {
                        display: none;
                    }
                }

                .form-section {
                    margin-bottom: 24px;
                    padding: 20px;
                    background: #f9fafb;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                }

                .form-section h4 {
                    margin: 0 0 16px 0;
                    color: #374151;
                    font-size: 16px;
                    font-weight: 600;
                }

                .grid {
                    display: grid;
                    gap: 16px;
                }

                .grid-cols-2 {
                    grid-template-columns: 1fr 1fr;
                }

                .form-help {
                    display: block;
                    margin-top: 4px;
                    font-size: 12px;
                    color: #6b7280;
                    line-height: 1.4;
                }

                @media (max-width: 768px) {
                    .grid-cols-2 {
                        grid-template-columns: 1fr;
                        width: auto;
                    }

                    .modal-actions {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
}

export default Gastos;
